import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";

export const OPEN_SEC_DOC_EVENT = "ortex:open-sec-doc";

/** Opens SEC filing documents in-app instead of navigating to sec.gov.
 *
 * Mounted once in _app; anything (e.g. the DataGrid's SEC Filing cells)
 * can open it by dispatching:
 *   window.dispatchEvent(new CustomEvent(OPEN_SEC_DOC_EVENT, { detail: { url, docId, title } }))
 * sec.gov cannot be iframed cross-site (Akamai answers cookieless
 * third-party frame requests with an empty 403), so with a docId the
 * iframe embeds our backend's document proxy instead; the header keeps an
 * escape hatch to the source. The iframe is fully sandboxed (no scripts,
 * opaque origin) because the proxied markup is third-party content.
 */
const SecFilingModal = () => {
    const [url, setUrl] = useState<string>("");
    const [docId, setDocId] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [fullScreen, setFullScreen] = useState(false);

    useEffect(() => {
        const onOpen = (event: Event) => {
            const detail = (event as CustomEvent).detail || {};
            if (typeof detail.url === "string" && detail.url.startsWith("https://www.sec.gov/")) {
                setUrl(detail.url);
                setDocId(detail.docId != null ? String(detail.docId) : "");
                setTitle(typeof detail.title === "string" ? detail.title : "SEC Filing");
            }
        };
        window.addEventListener(OPEN_SEC_DOC_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_SEC_DOC_EVENT, onOpen);
    }, []);

    const close = () => {
        setUrl("");
        setDocId("");
        setFullScreen(false);
    };

    const frameSrc = docId ? `${process.env.NEXT_PUBLIC_API_URL || ""}/interface/api/institutional/activist_filings/doc?id=${docId}` : url;

    return (
        <Dialog open={!!url} onClose={close} fullWidth maxWidth="xl" fullScreen={fullScreen}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {title}
                </Typography>
                <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" title="Open on sec.gov">
                    <OpenInNewIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setFullScreen((v) => !v)} title={fullScreen ? "Exit full screen" : "Full screen"}>
                    {fullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={close} title="Close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <DialogContent sx={{ p: 0, height: fullScreen ? "100%" : "82vh" }}>
                {url && (
                    <Box
                        component="iframe"
                        src={frameSrc}
                        title={title}
                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                        sx={{ width: "100%", height: "100%", border: 0, backgroundColor: "#fff" }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SecFilingModal;
